/* eslint-disable @typescript-eslint/no-shadow */
import dayjs, { Dayjs } from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Connection } from 'mysql2/promise';
import { v4 } from 'uuid';
import { FeatureFlags } from '../constants/flags';
import { ACTIVITY_CHANGE_TYPE, STRING_BOOLEAN_HASH } from '../constants/globals';
import { ChiroUpDayJsCommon } from '../constants/stringConstants';
import { AutoAppliedInsuranceSource } from '../enums/AutoAppliedInsuranceSource.enum';
import { CaseSubType } from '../enums/CaseSubType.enum';
import { ChiroUpJSON } from '../functions/ChiroUpJSON';
import { DisplayDataRecord } from '../functions/normalizeDisplayData';
import { createDayjs } from '../functions/time';
import { TransactionMagicEvent } from '../services/businessRules.service/transactionMagicCommon';
import { AppointmentInsuranceType, AppointmentStatuses } from './Appointment.type';
import { CodeSets } from './BillingCode.type';
import { Invoice, TransactionListInvoice } from './Invoice.type';
import { PatientPackage } from './Packages.type';
import { Patient } from './Patient.type';
import { Insurance } from './PatientInsurance.type';
import { PatientNote } from './PatientNote.type';
import {
  PatientPayment,
  PaymentType,
  PaymentTypePatient,
  PayorPaymentTypesHash,
  TransactionPayment,
} from './PatientPayment.type';
import { Payor, PayorLineItemType } from './Payor.type';

dayjs.extend(utc);
dayjs.extend(tz);

export enum TransactionListUsage {
  PatientPurchases = 'patient-purchases',
  BillingLedgerPurchases = 'billing-ledger-purchases',
}

export type TransactionPayorCodeType = {
  code: string;
  allowedAmount: number;
  payorId: number;
  replacementCode?: string | null;
};

export type ConsolidatedTransactionGetResponse = {
  items: PatientTransaction[];
  policies?: Insurance[];
  group?: any[];
  patient: Patient;
  memos?: { data: PatientNote[]; skip?: number; limit?: number };
  patientPackages?: {
    packages: PatientPackage[];
    servicesCovered: { [key: number]: number[] };
    treatmentsCovered: { [key: number]: number[] };
  };
};

export type PatientBalanceTransferProps = {
  amount?: number;
  referenceNumber?: string;
  provider?: {
    id: string;
    displayName: string;
    supervisedId?: string;
    supervisedName?: string;
  };
  date?: string | undefined;
  description?: string;
  locationId?: number;
};

/**
 * This is the minimal information to build a transaction.
 */
export type TransactionPatientSummaryType = {
  id: string;
  status: AppointmentStatuses;
  billingKey?: string;
  clinicId?: number;
  locationId?: number;
  appointmentDate?: Date | Dayjs | number | string;
  fname: string;
  lname: string;
  pname?: string;
  phone: string;
  email: string;
  displayName: string;
  clinician?: {
    id?: string;
    name?: string;
    supervisedId?: string;
    supervisedName?: string;
  };
  profilePhoto?: string;
  dateOfBirth?: string;
  accountNumber?: string;
  treatmentId?: number;
  disciplineId?: number;
};

export enum TransactionTypeEnum {
  Hybrid = 'Hybrid',
  Encounter = 'Encounter',
  AdHoc = 'AdHoc',
  Payment = 'Payment',
  Credit = 'Credit',
}

export const BillableTransactionType = {
  [TransactionTypeEnum.Encounter]: true,
  [TransactionTypeEnum.Hybrid]: true,
};

export const BillableTransactionTypes = Object.keys(BillableTransactionType);

export const isaPurchaseTransaction = (t: PatientTransaction) => {
  return t.type !== TransactionTypeEnum.Payment && t.type !== TransactionTypeEnum.Credit;
};

/**
 * [8746744786 by Brian] Added a guard so this does not run for
 * canceled or no-show appointments. Some of the "magic" rules
 * might flub up the transactions otherwise.
 *
 * @param t
 * @returns
 */
export const isaTreatmentTransactionType = (t: PatientTransaction) => {
  return (
    (t.type === TransactionTypeEnum.Encounter || t.type === TransactionTypeEnum.Hybrid) &&
    t.status !== AppointmentStatuses.Canceled &&
    t.status !== AppointmentStatuses.NoShow
  );
};

export const isaDebitTransactionType = (t: PatientTransaction) => {
  return (
    t.type === TransactionTypeEnum.Hybrid ||
    t.type === TransactionTypeEnum.AdHoc ||
    t.type === TransactionTypeEnum.Encounter
  );
};

export enum TransactionPurchaseSubtypeEnum {
  BalanceTransfer = 'BalanceTransfer',
  PurchasePackage = 'PurchasePackage',
}

export type TransactionPatientBriefSummaryType = Partial<
  Omit<TransactionPatientSummaryType, 'status'>
>;

export type ProviderOnTransactionType = {
  id: string;
  displayName: string;
  supervisedId?: string;
  supervisedName?: string;
  primaryId?: string | null;
};

export enum TransactionAdjustmentTypeEnum {
  ReversePaymentLineItem = 'ReversePaymentLineItem',
}

export type TransactionAdjustmentType = {
  ts: number;
  user: { id: string; displayName: string };
  items: {
    type: TransactionAdjustmentTypeEnum;
    description: string;
    data: any;
  }[];
};

export type TransactionSummaryType = {
  notes?: string[];
  items?: string[];
  deletedItems?: Partial<PatientTransactionItemType>[];
  adjustments?: TransactionAdjustmentType[];
  briefItems?: string[];
  lock?: {
    postClaim?: PostClaimLock;
  };
  claimResponseEraId?: number; // on payor payment transactions: the ClaimResponseEra this payment posted.
  packageContext?: Record<number, { coveredCodes: string[] }>;
};

export type PostClaimLockEntry = {
  idempotencyKey: string;
  timestamp: number; // Unix milliseconds
  userId: number;
  userName: string;
};

export type PostClaimLock = {
  inProgress: Record<string, PostClaimLockEntry>;
  completed: Record<string, PostClaimLockEntry>;
};

export type TransactionActionType = {
  id?: string | null;
  ts?: number | null;
  displayName?: string | null;
};

/**
 * [2025-08-22.0845 by Brian] There are only TWO write-off enums, however,
 * there are three types of write offs (#writeoff).
 *
 *   1) Patient write-off: subtype === PatientWriteOff
 *   2) Payor write-off: subtype === PayorWriteOff && payorId is not null
 *   3) Purchase write-off: subtype === PayorWriteOff && payorId is null
 */
export enum TransactionItemSubtypeEnum {
  AdditionalFee = 'AdditionalFee',
  Adjustment = 'Adjustment',
  AdjustmentPackage = 'AdjustmentPackage', // This subtype is a working subtype used in tax calculation but should never actually be written to the database as an item subtype
  AppliedPayment = 'AppliedPayment',
  BalanceAllocatedToPatient = 'BalanceAllocatedToPatient',
  BalanceTransfer = 'BalanceTransfer',
  Canceled = 'Canceled',
  IncentivePayment = 'IncentivePayment',
  NoShow = 'NoShow',
  Override = 'Override',
  Package = 'Package',
  PatientCredit = 'PatientCredit',
  PatientFeeRefund = 'PatientFeeRefund',
  PatientPayment = 'PatientPayment',
  PatientRefund = 'PatientRefund',
  PatientService = 'PatientService',
  PatientWriteOff = 'WriteOff',
  Payment = 'Payment',
  PayorWriteOff = 'PayorWriteOff',
  PayorWriteOffReversal = 'PayorWriteOffReversal',
  ProcessingFee = 'ProcessingFee',
  RefundUnappliedPayment = 'RefundUnappliedPayment',
  Reprocessed = 'Reprocessed',
  Service = 'Service',
  Supplement = 'Supplement',
  Tax = 'Tax',
  Treatment = 'Treatment',
  AddTreatment = 'AddTreatment',
}

export const paymentSubtypes = [
  TransactionItemSubtypeEnum.PatientPayment,
  TransactionItemSubtypeEnum.Payment,
  TransactionItemSubtypeEnum.AppliedPayment,
];

/**
 * Subtypes that represent a refund of money back to the patient. A partial
 * credit-card refund writes TWO rows: a PatientRefund row for the principal
 * returned to the patient and a PatientFeeRefund row for the proportional
 * processing-fee portion. They mean different things to the ledger:
 *   - Principal rows REDUCE the unposted/available balance (money that was
 *     applicable to purchases is no longer available).
 *   - Fee rows DO NOT reduce the unposted balance (the fee was never part
 *     of the apply-able pool — it was tracked separately via the
 *     ProcessingFee item).
 */
export const ItemRefundSubtypes: Record<string, true> = {
  [TransactionItemSubtypeEnum.PatientRefund]: true,
  [TransactionItemSubtypeEnum.PatientFeeRefund]: true,
};

export const isaRefundItem = (item: { subtype?: string | null } | null | undefined): boolean => {
  return ItemRefundSubtypes[item?.subtype ?? ''] === true;
};

export const ItemRefundSubtypesSqlIn = Object.keys(ItemRefundSubtypes)
  .map(k => `'${k}'`)
  .join(', ');

/**
 * True when the item is the processing-fee portion of a partial refund.
 * These rows DO NOT reduce the unposted balance; they exist purely to track
 * how much fee was returned to the patient's card.
 */
export const isFeeRefundItem = (item: { subtype?: string | null } | null | undefined): boolean => {
  return item?.subtype === TransactionItemSubtypeEnum.PatientFeeRefund;
};

/**
 * True when the item is a principal refund row (the amount returned to the
 * patient that came out of the apply-able payment pool).
 */
export const isPrincipalRefundItem = (
  item: { subtype?: string | null } | null | undefined
): boolean => {
  return item?.subtype === TransactionItemSubtypeEnum.PatientRefund;
};

export const TransactionItemSubtypeWriteOffs = {
  [TransactionItemSubtypeEnum.PatientWriteOff]: TransactionItemSubtypeEnum.PatientWriteOff,
  [TransactionItemSubtypeEnum.PayorWriteOff]: TransactionItemSubtypeEnum.PayorWriteOff,
};
export const TransactionItemSubtypeAdjustments = {
  [TransactionItemSubtypeEnum.Adjustment]: TransactionItemSubtypeEnum.Adjustment,
  [TransactionItemSubtypeEnum.Override]: TransactionItemSubtypeEnum.Override,
};

export const TransactionItemSubtypePayments = {
  [TransactionItemSubtypeEnum.Payment]: TransactionItemSubtypeEnum.Payment,
  [TransactionItemSubtypeEnum.PatientPayment]: TransactionItemSubtypeEnum.PatientPayment,
  [TransactionItemSubtypeEnum.AppliedPayment]: TransactionItemSubtypeEnum.AppliedPayment,
} as { [key: string]: string };

const TransactionItemSubtypeService = {
  [TransactionItemSubtypeEnum.Service]: TransactionItemSubtypeEnum.Service,
  [TransactionItemSubtypeEnum.PatientService]: TransactionItemSubtypeEnum.PatientService,
} as { [key: string]: string };

export const isaTaxItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return item?.subtype === TransactionItemSubtypeEnum.Tax;
};

export const notaTaxItem = (item: PatientTransactionItemType | null | undefined) => {
  return !isaTaxItem(item);
};

/**
 * Currently excludes:
 * - Void items (voided transactions should not count toward balance)
 * - Tax items (handled separately in tax calculations)
 */
export const isaBalanceCalculationExcludedItem = (
  item: PatientTransactionItemType | null | undefined
) => {
  if (!item) return false;

  return (
    item.type === TransactionItemTypeEnum.Void || item.subtype === TransactionItemSubtypeEnum.Tax
  );
};

export const isaServiceItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return !!TransactionItemSubtypeService[item.subtype];
};

export const isaPackageApplied = (items: PatientTransactionItemType[] | null | undefined) => {
  if (!items) return false;
  if (!Array.isArray(items)) return false;
  if (items.length === 0) return false;

  return items.some(
    item => item?.subtype === TransactionItemSubtypeEnum.Adjustment && !!item?.packageId
  );
};

export const isaPackageItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return item?.subtype === TransactionItemSubtypeEnum.Adjustment && !!item?.packageId;
};

/**
 * The original-purchase line for a package (Debit, subtype === 'Package').
 * Distinct from `isaPackageItem`, which identifies a package being *applied*
 * (a credit Adjustment carrying a packageId). Treated as a service for sales-
 * tax purposes when the Package's `collectTax` flag is on.
 */
export const isaPackagePurchaseItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return item?.subtype === TransactionItemSubtypeEnum.Package;
};

export const isaSupplementItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return item?.subtype === TransactionItemSubtypeEnum.Supplement;
};

const statementItemDebitSubtypes = {
  [TransactionItemSubtypeEnum.Canceled]: true,
  [TransactionItemSubtypeEnum.NoShow]: true,
  [TransactionItemSubtypeEnum.Package]: true,
  [TransactionItemSubtypeEnum.Supplement]: true,
  [TransactionItemSubtypeEnum.BalanceTransfer]: true,
} as STRING_BOOLEAN_HASH;

export const isaStatementDebitItem = (item: PatientTransactionItemType | null | undefined) => {
  return statementItemDebitSubtypes?.[item?.subtype ?? '_'] === true;
};

export const notaServiceItem = (item: PatientTransactionItemType | null | undefined) => {
  return !isaServiceItem(item);
};

export const isaPaymentItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return Boolean(TransactionItemSubtypePayments[item.subtype]);
};

export const isaPatientPaymentItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;

  return item?.type === TransactionItemTypeEnum.Credit && isaPaymentItem(item) && !item.payorId;
};

export const isaProductItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return item?.subtype === TransactionItemSubtypeEnum.Supplement;
};

export const isaTreatmentItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return (
    item.subtype === TransactionItemSubtypeEnum.Treatment ||
    item.subtype === TransactionItemSubtypeEnum.AddTreatment
  );
};

export const isaDebitItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.type) return false;

  return item.type === TransactionItemTypeEnum.Debit;
};

/**
 * Items that can be replaced/offset by Override items.
 * Only specific debit subtypes should be offset when overrides are applied.
 * Excludes items like Reprocessed (purchase reversals) which are paired with payments.
 */
export const isaOverridableItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.type || !item.subtype) return false;

  return (
    item.type === TransactionItemTypeEnum.Debit &&
    (item.subtype === TransactionItemSubtypeEnum.Service ||
      item.subtype === TransactionItemSubtypeEnum.PatientService ||
      item.subtype === TransactionItemSubtypeEnum.Supplement ||
      item.subtype === TransactionItemSubtypeEnum.Treatment ||
      item.subtype === TransactionItemSubtypeEnum.AddTreatment)
  );
};

export const isaBriefDescriptionItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return (
    item.subtype === TransactionItemSubtypeEnum.Treatment ||
    item.subtype === TransactionItemSubtypeEnum.AddTreatment ||
    item.subtype === TransactionItemSubtypeEnum.Supplement ||
    item.subtype === TransactionItemSubtypeEnum.Package ||
    item.subtype === TransactionItemSubtypeEnum.NoShow ||
    item.subtype === TransactionItemSubtypeEnum.Canceled
  );
};

export const isaAddedTreatmentItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return item.subtype === TransactionItemSubtypeEnum.AddTreatment;
};

export const isAPackageApplied = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return item.subtype === TransactionItemSubtypeEnum.Adjustment && !!item?.packageId;
};

export const isNotaServiceItem = (item: PatientTransactionItemType | null | undefined) => {
  return !isaServiceItem(item);
};

// Any debit item that is a service item.
export const isaInsuranceBillableItem = (item: PatientTransactionItemType | null | undefined) => {
  return (
    item?.type === TransactionItemTypeEnum.Debit &&
    item?.subtype === TransactionItemSubtypeEnum.Service
  );
};

// Any debit item that is not a service item or tax.
export const isNotaInsuranceBillableItem = (
  item: PatientTransactionItemType | null | undefined
) => {
  return (
    item?.type === TransactionItemTypeEnum.Debit &&
    item?.subtype !== TransactionItemSubtypeEnum.Service &&
    item?.subtype !== TransactionItemSubtypeEnum.Tax
  );
};

export const isNotaInsuranceBillableItemOrTax = (
  item: PatientTransactionItemType | null | undefined
) => {
  return isNotaInsuranceBillableItem(item) && !isaTaxItem(item);
};

export enum TransactionItemTypeEnum {
  Credit = 'Credit',
  Debit = 'Debit',
  Void = 'Void',
}

export enum AdjustmentStructureEnum {
  DollarDiscount = '$ Discount',
  DollarOverride = '$ Override',
  PercentDiscount = '% Discount',
}
export enum TaxRateBasisEnum {
  Service = 's',
  Product = 'p',
  NoTax = 'z',
}

// Membership enrollment configuration captured on the purchase form. Rides on the
// Package line item of the enrollment purchase; consumed server-side by
// createPatientPackage (recurringPaymentMethod snapshot + RecurringPaymentSchedules row).
export type MembershipSetup = {
  // cash | check | creditCard | creditCardExternal (creditTransfer is not allowed).
  paymentMethod: PaymentTypePatient | null;
  // Dejavoo merchant/credential recurring billing runs under (PaymentProcessorCredentials.id).
  credentialId?: number | null;
  // Saved card to charge (DejavooCustomerTokens.id).
  storedTokenId?: number | null;
  // Frontend orchestration flag: collect the enrollment payment at save.
  collectEnrollmentPayment?: boolean;
};

// sql/ddl/PatientTransactionItem.my.sql
export type PatientTransactionItemType = {
  id: number;
  // paymentId?: number | null;
  // payorReferenceId?: string | null;
  adjustment?: {
    percentOff?: number;
    structure: string;
    taxRate?: TaxRateBasisEnum;
  };
  adjustmentId?: number | null;
  amount: number | string | null;
  code?: string | null;
  codeSet?: string | null;
  createdAt?: Date | number | string;
  description?: string | null;
  diagnoses?: PatientTransactionItemDiagnosisType[] | null;
  /**
   * Display data for the transaction item.
   * - Can be a JSON string (when fetched from TEXT column in database)
   * - Can be a parsed object (after processing with normalizeDisplayData)
   * - Contains stableId and invoiceData for preserving custom invoice fields
   */
  displayData?: string | DisplayDataRecord | null;
  // finalAdjudicated?: number;
  // finalAdjudicatedPatientResponsibility?: number;
  // finalAdjudicatedByPayor?: {
  //   [key: string]: {
  //     finalAdjudicated: number;
  //     finalAdjudicatedPatientResponsibility: number;
  //   };
  // };
  groupId?: string | null;
  insuranceBillable?: boolean;
  invoiceId?: number | null;
  isDuplicate?: boolean;
  lineItemControlNumber?: string | null;
  locked?: boolean | false;
  method?: string | null;
  modifier1?: string | null;
  modifier2?: string | null;
  modifier3?: string | null;
  modifier4?: string | null;
  isDrugCode?: boolean;
  nationalDrugCode?: string | null;
  ndcUnits?: number | null;
  ndcUnitType?: string | null;
  notes?: string[] | null;
  nonBillable?: number;
  // Membership enrollment configuration riding on the Package line item. Not persisted
  // on PatientTransactionItem — the durable copy is the PatientPackages snapshot and
  // the RecurringPaymentSchedules row created by createPatientPackage.
  membershipSetup?: MembershipSetup | null;
  // Display-only marker for an optimistic enrollment payment row shown while the charge
  // is in flight. Never persisted or saved — it lives only in the ledger overlay and is
  // replaced by the real payment (or removed) once the charge resolves.
  pendingEnrollment?: boolean;
  packageDate?: number | string | null;
  packageId?: number | null;
  parentPackageId?: number | null;
  patientPackageId?: number | null;
  patientTransactionId: number;
  paymentSource?: string;
  paymentToward?: string;
  payorId?: number | null; // TODO!!!! [Brian, these are crap. No such column.]
  payorName?: string | null; // TODO!!!! [Brian, these are crap. No such column.]
  // payors?: Payor[] | null; // TODO!!!! [Brian, these are crap. No such column.]
  productTaxRate?: number;
  salesTax?: boolean;
  serviceTaxRate?: number;
  siblings?: number[] | null;
  stableId?: string | null; // Durable line identity (= displayData.stableId; survives save cycles, PTI.id does not. filledon write. NOT indexed.
  subtype: TransactionItemSubtypeEnum | null;
  supplementId?: number;
  taxAmount?: number | string | null;
  subtypeId?: number | null;
  type: TransactionItemTypeEnum | null;
  units: number | null;
  variableBilledAmount?: boolean;
};

// sql/ddl/PatientTransactionItemDiagnosis.my.sql
export type PatientTransactionItemDiagnosisType = {
  id: number;
  itemId: number;
  code: string;
  codeSet: CodeSets;
  description: string;
  ord: number;
};

// Path: sql/ddl/PatientTransaction.my.sql
/**
 * When one of these is being viewed in the UI, the items
 * are done separately.
 */
export type PatientTransactionSummary = {
  id: number;
  number?: string | null;
  billingKey: string;
  clinicId: number;
  locationId: number;
  type: TransactionTypeEnum;
  subtype?: PaymentType | null;
  balance?: number;
  startingBalance?: number;
  processingFee?: number;
  created: TransactionActionType;
  updated: TransactionActionType;
  patient?: PatientOnTransactionType;
  provider?: ProviderOnTransactionType;
  contractingProviderId?: string;
  summary?: TransactionSummaryType;
  description?: string;
};

export type TransactionAppendItemsType = {
  services?: PatientTransactionItemType[];
  insurances?: AppointmentInsuranceType[] | Partial<AppointmentInsuranceType>[];
  courtesyBilling?: boolean;
  superBill?: boolean;
  newTransaction?: PatientTransaction;
};

export type PatientTransaction = PatientTransactionSummary & {
  // [key: string]: any;
  allocateToPatient?: boolean;
  allocatedFromClaimsAmount?: number;
  allowBillingPriorityChange?: boolean;
  amount?: number;
  assessmentCodes?: any[];
  billingProfileId?: number;
  caseSubtype?: CaseSubType | null;
  caseTypeId?: number | null;
  courtesyBilling?: boolean;
  disciplineId?: number | null;
  ePayBatchKey?: string;
  ePayRefNum?: string;
  finalized?: boolean;
  groupId?: number | null | undefined;
  hasAppointment?: boolean;
  appointmentDeletedAt?: number | null;
  hasEncounter?: boolean;
  encounterSigned?: boolean;
  insurances?: AppointmentInsuranceType[] | Partial<AppointmentInsuranceType>[];
  isBillingStarted?: boolean;
  isVoided?: boolean;
  items: PatientTransactionItemType[];
  locked?: boolean;
  nextRequestedEncounterDate?: number;
  patientBalance?: number | string;
  patientBilledAmount?: number | string;
  patientCaseTypeId?: number | null;
  patientSummary?: TransactionPatientBriefSummaryType | null;
  payments?: PatientPayment[];
  payorId?: number | null;
  payors?: Payor[];
  productTaxRate?: number;
  providerSummary?: any;
  providerCanChange?: boolean;
  purchases?: PatientTransaction[];
  receiptDetail?: ReceiptDetails;
  referenceNumber?: string;
  services?: PatientTransactionItemType[];
  servicesDirty?: boolean | null;
  serviceTaxRate?: number;
  status?: AppointmentStatuses | null;
  superBill?: boolean;
  taxDirty?: boolean | null;
  total?: number;
  transactionDate?: number | null;
  treatmentId?: number | null;
  tz?: string;
  verified?: number | null;
  verifiedBy?: string | null;
  // Keeps from having to get the Encounter just for the visitDate.
  visitDate?: number | null;
  appointmentClinicianId?: string | null;
  merged?: string | null;
  startTime?: number | null;
  paymentAmount?: number;
  hasBeenLocked?: boolean;
  hasDeductedInsurancePolicy?: boolean;
  paidForByBillingKey?: string | null;
  payorLineItems?: PayorLineItemType[];
  parentDisciplineId?: number | null;
  hasPostedClaims?: boolean;
  /** Lock state transition - set by savePatientTransaction, used by consolidatedTransactionUpdate for auto-837 */
  lockTransition?: {
    oldTransactionWasLocked: boolean;
    newTransactionIsLocked: boolean;
  };
};

/**
 * This is the raw row from the database table.
 */
export type PatientTransactionRowType = {
  id: number;
  number?: string | null;
  status?: string;
  taxDirty?: boolean;
  servicesDirty?: boolean | null;
  billingKey: string;
  clinicId: number;
  locationId: number;
  tz?: string;
  type?: TransactionTypeEnum;
  subtype?: PaymentType | null;
  balance?: number;
  startingBalance?: number;
  processingFee?: number;
  createdId?: string | null;
  updatedId?: string | null;
  patientId?: string | null;
  patientSummary?: string;
  providerId?: string;
  providerSummary?: string;
  contractingProviderId?: string;
  summary?: string;
  createdAt?: number | null;
  updatedAt?: number | null;
  transactionDate: number;
  productTaxRate?: number;
  serviceTaxRate?: number;
  ePayRefNum?: string;
  ePayBatchKey?: string;
  referenceNumber?: string;
  description?: string;
  disciplineId?: number | null;
  treatmentId?: number | null;
  courtesyBilling?: boolean;
  superBill?: boolean;
  billingProfileId?: number;
  hasEncounter?: boolean;
  hasAppointment?: boolean;
  encounterSigned?: boolean;
  patientCaseTypeId?: number | null;
  caseTypeId?: number | null;
  caseSubtype?: CaseSubType | null;
  nextRequestedEncounterDate?: number;
  verified?: number | null;
  verifiedBy?: string | null;
  appointmentClinicianId?: string | null;
  merged?: string | null;
  hasBeenLocked?: boolean;
  paidForByBillingKey?: string | null;
};

export enum TransactionActivityType {
  Created = 'transaction-created',
  Updated = 'transaction-updated',
  PayorInvoiced = 'payor-invoiced',
  /**
   * Story 12074868133 — a policy was added/replaced/refreshed on this
   * purchase via the auto-apply modal's Section 2 bulk-apply (originating
   * from a different purchase). The `data.context` carries the source
   * billingKey and the slot outcome so investigators can trace cross-
   * purchase changes.
   */
  BulkInsuranceApplied = 'bulk-insurance-applied',
}

export const getTransactionActivityDisplay = (
  activity: PatientTransactionActivity | undefined | null
) => {
  if (!activity) return 'No activity found';

  switch (activity.type) {
    case TransactionActivityType.Created:
      return 'Created';
    case TransactionActivityType.Updated:
      return 'Updated';
    case TransactionActivityType.PayorInvoiced:
      return 'Payor invoiced';
    case TransactionActivityType.BulkInsuranceApplied:
      return 'Insurance applied (bulk)';
    default:
      return 'Unknown activity';
  }
};

export type PatientTransactionActivity = {
  id?: string;
  billingKey: string;
  clinicId: number;
  invoiceId?: number;
  billingKeyAllInvoices?: string;
  createdAt?: number;
  updated: TransactionActionType;
  description: string;
  type: TransactionActivityType;
  data?: {
    updates?: ACTIVITY_CHANGE_TYPE[];
    context?: TransactionActivityContext;
    strings?: string[];
  };
  version?: number; // A way to distinguish formatting changes.
};

/**
 * Context for transaction activity hash calculation.
 * All fields here are used to generate a deterministic activity ID.
 * When adding new fields, ensure they are also added to the keyObject in createTransactionActivity.
 */
export type TransactionActivityContext = {
  // ERA processing context
  exchangeId?: string;
  claimControlNumber?: string;
  interchangeControlNumber?: string;
  claimResponseEraId?: number;
  // Invoice status change context
  type?: string;
  ts?: string;
  previous?: string;
  final?: string;
  // Additional context (not used in hash, but stored for reference)
  [key: string]: unknown;
};

/**
 * This is a special-case of a PaymentTransactionItem. Simplifies
 * the payment view.
 */
export type PatientPaymentType = {
  id: number;
  number?: string | null;
  billingKey: string;
  amount: number;
  processingFee: number;
  balance: number;
  paymentDate: string;
  isRefunded?: boolean;
  isPartiallyRefunded?: boolean;
  isPending?: boolean;
  isVoided?: boolean;
  description: string;
  type: TransactionTypeEnum;
  receiptDetail: ReceiptDetails;
  processor?: string;
  paidForByBillingKey?: string | null;
  paidByPatientId?: string;
  paidByPatientName?: string;
  isAppliedFromRelated?: boolean; // related patient's credit applied to this patient's purchase
};

export type getPatientTransactionOptionsType = {
  rowOnly?: boolean;
  include?: {
    allowBillingPriorityChange?: boolean;
    allowedAmounts?: boolean;
    assessmentCodes?: boolean;
    diagnoses?: boolean;
    eraIds?: boolean;
    insurance?: boolean;
    insuranceBillableItems?: boolean;
    invoices?: boolean;
    purchases?: boolean;
    payors?: boolean;
    services?: boolean;
    itemsOnPurchases?: boolean;
    payorLineItems?: boolean;
  };
};

export const GetPatientTransactionInsuranceOnlyOptions: getPatientTransactionOptionsType = {
  rowOnly: false,
  include: {
    allowBillingPriorityChange: false,
    allowedAmounts: false,
    assessmentCodes: false,
    diagnoses: false,
    eraIds: false,
    insurance: true,
    insuranceBillableItems: false,
    invoices: false,
    purchases: false,
    payors: false,
    services: false,
    itemsOnPurchases: false,
    payorLineItems: false,
  },
};

export type PatientTransactionSaveOptionsType = {
  engrams?: any;
  resaveBillingKey?: string;
  forcePersistSummary?: boolean;
  updatePatientDisplayName?: boolean;
  signatureChangeConfirmed?: boolean;
};

export type PatientTransactionSaveType = {
  allItemsOnConsolidatedTransaction?: PatientTransactionItemType[];
  bypassInsuranceRulesCheck?: boolean;
  bypassPostedClaimsCheck?: boolean;
  calledBy?: string;
  connection: Connection;
  ddb?: any;
  firstSlot?: boolean;
  forceUpdate?: boolean;
  getPatientTransactionOptions?: getPatientTransactionOptionsType;
  hasAccess?: (feature: FeatureFlags) => boolean;
  insurances?: AppointmentInsuranceType[];
  magicEvent?: TransactionMagicEvent;
  mockError?: boolean;
  noShowAppointment?: boolean;
  previousSavedConsolidatedItems?: PatientTransactionItemType[];
  previousTransaction?: PatientTransaction; // We can pass an old one if we've just gotten it.
  primaryClinicianId?: string;
  purpose?: 'update' | undefined | null;
  /**
   * Force re-evaluation of billing supervision against the rendering
   * clinician's CURRENT UserClinic settings, regardless of the status-derived
   * `isCheckingIn` inference. Check-in callers set this so a supervisor that
   * was added or removed since booking is reflected on the purchase. See the
   * supervision block in `savePatientTransaction`.
   */
  reevaluateSupervision?: boolean;
  savePatientBalance?: boolean;
  savingAsNew?: boolean;
  skipSaveIfNotDirty?: boolean;
  autoAppliedInsurances?: AutoAppliedInsuranceSource;
  syncPatientSummary?: boolean;
  timed?: boolean;
  trace?: boolean;
  transaction?: PatientTransaction;
  updated: TransactionActionType;
};

/**
 * This type represents a transaction with associated invoices as returned from the database
 * in the transaction list API. It extends PatientTransaction with additional properties
 * needed for displaying transactions with invoice data.
 */
export type TransactionWithInvoices = PatientTransaction & {
  // Invoice-related properties
  invoices?: TransactionListInvoice[]; // Array of invoice objects
  total?: number; // Total amount across all invoices
  outstanding?: number; // Outstanding amount across all invoices
  itemColumn?: string[]; // For display in the UI
  displayName?: string; // For display in the UI
};

// export type PatientTransactionPayment = PatientTransactionItemType & {
//   paymentAmount: number;
//   transactionId: number;
//   summary?: { items: string[] };
// }
export type RefundRecord = {
  amount: number;
  /** Portion of the processing fee refunded on this call (CUP-0095). */
  feeRefunded?: number;
  date: string;
  method: string;
  refundedBy: {
    id: string;
    displayName: string;
  };
};

export type ReceiptDetails = {
  summary: {
    paymentTotal: number;
    total: {
      amount: number;
      label: string;
    };
    method: {
      type: string;
      label: string;
      /** Last 4 digits of the credit card charged. Only set for credit-card payments. */
      cardLastFour?: string;
      /** Saved-card nickname (e.g. "Personal Visa") when charged against a stored card. */
      cardNickname?: string;
      /** Cardholder name as it appears on the card (e.g. "Candy Apple"). */
      cardholderName?: string;
      /** Card brand: "Visa", "Mastercard", "Amex", etc. */
      cardType?: string;
    };
    totalApplied: number;
    invoiceTotals: number;
    remainingBalance: number;
    paymentDate: string;
    processingFee: {
      label: string;
      amount: number;
    };
    totalRefunded?: number;
    /** Sum of feeRefunded across all refund records (CUP-0095). */
    totalFeeRefunded?: number;
  };
  transactionPayments: TransactionPayment[];
  invoices: Partial<Invoice>[];
  isRefunded: boolean;
  isVoided: boolean;
  number?: string;
  refunds?: RefundRecord[];
  voidedDate?: string;
  voidedBy?: {
    id: string;
    displayName: string;
  };
};

export type PatientOnTransactionType = {
  id: string;
  fname?: string;
  lname?: string;
  phone?: string;
  email?: string;
  displayName: string;
  profilePhoto?: string;
  dateOfBirth?: string;
  accountNumber?: string;
};
export class PatientTransactionClass implements PatientTransaction {
  id: number;
  number?: string | null;
  transactionDate?: number | null;
  status?: AppointmentStatuses | null;
  taxDirty?: boolean | null;
  servicesDirty?: boolean | null;
  billingKey: string;
  clinicId: number;
  locationId: number;
  balance?: number;
  startingBalance?: number;
  // processingFee?: number;
  type: TransactionTypeEnum;
  subtype: PaymentType | null;
  created: TransactionActionClass;
  updated: TransactionActionClass;
  patient?: PatientOnTransactionType;
  provider?: ProviderOnTransactionType;
  contractingProviderId?: string;
  description?: string;
  items: any[]; // Can't stand it, had to go with any.
  summary: any;
  patientBalance?: number | string;
  courtesyBilling?: boolean;
  superBill?: boolean;
  billingProfileId?: number;
  onClick: (e: any, s: string) => void;
  tz?: string;
  patientCaseTypeId?: number | null;
  caseTypeId?: number | null;
  caseSubtype?: CaseSubType | null;
  nextRequestedEncounterDate?: number;
  verified?: number | null;
  verifiedBy?: string | null;
  appointmentClinicianId?: string | null;
  startTime?: number;
  hasBeenLocked?: boolean;

  constructor(
    row: PatientTransactionRowType,
    items: PatientTransactionItemType[],
    onClick?: (billingKey: string) => void
  ) {
    this.id = row.id;
    this.number = row.number;
    this.transactionDate = row.transactionDate;
    this.clinicId = row.clinicId;
    this.status = row.status as AppointmentStatuses;
    this.taxDirty = row.taxDirty;
    this.servicesDirty = row.servicesDirty;
    this.locationId = row.locationId;
    this.billingKey = row.billingKey;
    this.type = row.type as TransactionTypeEnum;
    this.subtype = row.subtype as PaymentType;
    this.balance = row.balance;
    this.startingBalance = row.startingBalance;
    this.nextRequestedEncounterDate = row.nextRequestedEncounterDate;
    this.verified = row.verified;
    this.verifiedBy = row.verifiedBy;
    // this.processingFee = row.processingFee;
    this.created = new TransactionActionClass({
      id: row.createdId,
      ts: row.createdAt,
    });
    this.updated = new TransactionActionClass({
      id: row.updatedId,
      ts: row.updatedAt,
    });
    this.patient =
      typeof row.patientSummary === 'string'
        ? ChiroUpJSON.parse(row.patientSummary)
        : row.patientSummary;
    this.provider =
      typeof row.providerSummary === 'string'
        ? ChiroUpJSON.parse(row.providerSummary)
        : row.providerSummary;
    this.contractingProviderId = row.contractingProviderId;
    this.courtesyBilling = row.courtesyBilling;
    this.superBill = row.superBill;
    this.items = items;
    this.billingProfileId = row.billingProfileId;

    if (onClick) {
      this.onClick = (_: any, s: string) => {
        onClick(s);
      };
    } else {
      this.onClick = () => {
        console.warn('no onClick');
      };
    }

    this.tz = row.tz;
    this.patientCaseTypeId = row.patientCaseTypeId;
    this.caseTypeId = row.caseTypeId;
    this.caseSubtype = row.caseSubtype;
    this.appointmentClinicianId = row.appointmentClinicianId;
    this.hasBeenLocked = row.hasBeenLocked;
  }

  static newRecord = ({
    billingKey,
    clinicId,
    locationId,
    type,
    created,
    provider,
    items,
    patient,
    courtesyBilling = false,
    superBill = false,
    tz = ChiroUpDayJsCommon.defaultTimezone,
    transactionDate = Date.now(),
  }: {
    billingKey: string;
    clinicId: number;
    locationId: number;
    type: TransactionTypeEnum;
    created: TransactionActionType;
    provider?: ProviderOnTransactionType;
    items?: PatientTransactionItemType[];
    patient?: PatientOnTransactionType;
    courtesyBilling?: boolean;
    superBill?: boolean;
    tz?: string;
    transactionDate?: number;
  }) => {
    return new this(
      {
        id: -1,
        number: null,
        billingKey,
        clinicId,
        locationId,
        balance: 0,
        courtesyBilling,
        superBill,
        type,
        createdAt: created.ts,
        createdId: created.id,
        updatedAt: null,
        updatedId: null,
        patientSummary: JSON.stringify(patient),
        providerSummary: JSON.stringify(provider),
        transactionDate,
        tz,
      },
      items || []
    );
  };

  static classFromObject = ({ transaction }: { transaction: PatientTransaction }) => {
    return new this(
      {
        id: transaction.id,
        number: transaction.number,
        transactionDate: transaction.transactionDate || 0,
        clinicId: transaction.clinicId,
        status: transaction.status as AppointmentStatuses,
        taxDirty: transaction.taxDirty ?? false,
        servicesDirty: transaction.servicesDirty ?? false,
        locationId: transaction.locationId,
        billingKey: transaction.billingKey,
        type: transaction.type,
        subtype: transaction.subtype,
        balance: transaction.balance,
        startingBalance: transaction.startingBalance,
        // processingFee: transaction.processingFee,
        createdId: transaction?.created?.id,
        createdAt: transaction?.created?.ts,
        updatedAt: transaction?.updated?.ts,
        updatedId: transaction?.updated?.id,
        patientSummary: JSON.stringify(transaction?.patient),
        providerSummary: JSON.stringify(transaction?.provider),
        courtesyBilling: transaction?.courtesyBilling,
        superBill: transaction?.superBill,
        tz: transaction?.tz,
        billingProfileId: transaction?.billingProfileId,
        nextRequestedEncounterDate: transaction?.nextRequestedEncounterDate,
        verified: transaction?.verified,
        verifiedBy: transaction?.verifiedBy,
        appointmentClinicianId: transaction?.appointmentClinicianId,
        hasBeenLocked: transaction?.hasBeenLocked,
      },
      transaction.items
    );
  };

  static initialFromPatientSummary = (
    patientSummary: TransactionPatientSummaryType,
    user: TransactionActionType,
    patient?: Patient
  ) => {
    const created = {
      id: user.id,
      displayName: user.displayName || 'Unknown.',
    };
    const provider = {
      id: patientSummary.clinician?.id,
      displayName: patientSummary.clinician?.name,
      supervisedId: patientSummary.clinician?.supervisedId,
      supervisedName: patientSummary.clinician?.supervisedName,
    };

    return {
      id: -1,
      billingKey: patientSummary.billingKey,
      clinicId: patientSummary.clinicId,
      locationId: patientSummary.locationId || -1,
      transactionDate: patientSummary.appointmentDate,
      balance: 0,
      startingBalance: 0,
      // processingFee: 0,
      type: TransactionTypeEnum.Hybrid,
      subtype: null,
      created,
      updated: created,
      patient: {
        id: patientSummary.id,
        fname: patientSummary?.fname,
        lname: patientSummary?.lname,
        pname: patientSummary?.pname,
        displayName: patientSummary.displayName,
        phone: patientSummary.phone || patient?.phone,
        email: patientSummary.email || patient?.email,
      },
      items: [],
      provider: provider,
      courtesyBilling: false,
      superBill: false,
      caseSubtype: CaseSubType.ActiveTreatment,
    } as any; // PatientTransaction
  };

  static initialFromPayment = (
    payment: PatientPayment,
    clinicId: number,
    user: TransactionActionType,
    credit: boolean,
    primaryTimezone: string,
    payorId?: number | null
  ) => {
    const billingKey = v4();

    const created = {
      id: user.id,
      ts: Date.now(),
      displayName: user.displayName || 'Unknown.',
    };

    let transactionDate = dayjs().tz(primaryTimezone).startOf('day').valueOf();

    if (typeof payment.paymentDate === 'string') {
      try {
        const convertedDate = dayjs(payment.paymentDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
        transactionDate = (
          createDayjs({
            datetime: convertedDate,
            timezone: 'UTC', // For date-only, we want UTC. No time please.
          }) as Dayjs
        )
          .startOf('day')
          .valueOf();
      } catch (e) {
        console.error(e);

        // It may be interesting to note that when date parses mm/dd/yyyy, it
        // is just a date. When it parses yyyy-mm-dd, it is a date and time so
        // we need to '00:00:00' at the end to make it leave it alone.
        try {
          transactionDate = dayjs
            .tz(`${payment.paymentDate} 00:00:00`, 'UTC')
            .startOf('day')
            .valueOf();
        } catch (e) {
          console.error(e);
          console.error(`Could not parse date: ${payment.paymentDate}.`);
        }
      }
    }

    const amount = Number(payment.amount) + Number(payment.processingFee || 0),
      items: any = [
        {
          id: -1,
          patientTransactionId: -1,
          description: `Initial payment${amount < 0 ? ' (rollback)' : ''}`,
          type: TransactionItemTypeEnum.Credit,
          subtype: TransactionItemSubtypeEnum.Payment,
          amount,
          units: 1,
          salesTax: 0,
        },
      ];

    const amountsPaid =
      payment.transactionPayments?.reduce((sum, tp) => sum + Number(tp.paymentAmount), 0) || 0;

    const final = {
      id: -1,
      number: null,
      billingKey,
      clinicId,
      description: payment.description,
      locationId: payment.locationId,
      // processingFee: payment.processingFee || 0,
      patient: !PayorPaymentTypesHash[payment.type as PaymentType]
        ? {
            id: payment.patientId,
            displayName: '',
          }
        : {
            id: payment.patientId,
            displayName: payment.description,
          },
      balance: payment.amount - amountsPaid,
      startingBalance: payment.amount + (payment.processingFee || 0),
      type: credit ? TransactionTypeEnum.Credit : TransactionTypeEnum.Payment,
      subtype: payment.type,
      amount: payment.amount,
      transactionDate,
      created,
      items,
      ePayRefNum: payment.ePayRefNum,
      ePayBatchKey: payment.ePayBatchKey,
      referenceNumber: payment?.referenceNumber ? String(payment?.referenceNumber) : null,
      tz: primaryTimezone,
      payorId,
    };

    return final as PatientTransaction;
  };
}

export const toTransactionItemSubtype = (
  subtype: TransactionItemSubtypeEnum | string,
  item: any
) => {
  switch (subtype) {
    case TransactionItemSubtypeEnum.Treatment: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        units: 1,
        salesTax: item.salesTax,
        amount: item.amount,
        subtypeId: item.subtypeId || null,
      };
    }
    case TransactionItemSubtypeEnum.AddTreatment: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        units: 1,
        salesTax: item.salesTax,
        amount: item.amount,
        subtypeId: item.subtypeId || null,
      };
    }
    case TransactionItemSubtypeEnum.PatientWriteOff: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: 'Write-off',
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Credit,
        subtype,
        units: 1,
        amount: item.amount,
      };
    }
    case TransactionItemSubtypeEnum.Supplement: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.name,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.price,
        units: item.units || 1,
        groupId: item.groupId,
        salesTax: item.salesTax,
        supplementId: item.ID,
      };
    }
    case TransactionItemSubtypeEnum.AdditionalFee: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.amount,
        units: item?.units ?? 1,
        groupId: item.groupId,
        salesTax: 0,
        packageId: item.packageId,
        parentPackageId: item?.parentPackageId,
      };
    }
    case TransactionItemSubtypeEnum.Service: {
      // a nonBillable code on the clinic's code list must not default
      // to an insurance-billable item (claims select by subtype 'Service').
      const insuranceBillable = item.insuranceBillable ?? !item.nonBillable;

      return {
        id: -1,
        patientTransactionId: -1,
        code: item.code,
        codeSet: item.codeSet,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype: insuranceBillable ? subtype : TransactionItemSubtypeEnum.PatientService,
        amount: item.billedAmount,
        units: 1,
        modifier1: item.modifier1,
        modifier2: item.modifier2,
        modifier3: item.modifier3,
        modifier4: item.modifier4,
        locked: false,
        groupId: item.groupId,
        salesTax: item.salesTax,
        insuranceBillable,
      };
    }
    case TransactionItemSubtypeEnum.Payment: {
      const amount = Number(item.paymentAmount) || Number(item.amount);

      return {
        id: item.id ?? -1,
        patientTransactionId: item.patientTransactionId ?? -1,
        description: `Payment${amount < 0 ? ' (rollback)' : ''}`,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Credit,
        subtype,
        amount,
        units: 1,
        salesTax: 0, //TODO Change this when I know more about tax
        paymentToward: item.paymentToward,
        paymentSource: item.paymentSource,
        displayData: item.displayData ? JSON.stringify(item.displayData) : null,
        payorId: item.payorId || null,
      };
    }
    case TransactionItemSubtypeEnum.IncentivePayment: {
      return {
        id: item.id ?? -1,
        patientTransactionId: item.patientTransactionId ?? -1,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Credit,
        subtype,
        amount: Number(item.paymentAmount) || Number(item.amount),
        units: 1,
        salesTax: 0, //TODO Change this when I know more about tax
        paymentToward: item.paymentToward,
        paymentSource: item.paymentSource,
        displayData: item.displayData ? JSON.stringify(item.displayData) : null,
      };
    }
    case TransactionItemSubtypeEnum.Override: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.description,
        type: TransactionItemTypeEnum.Debit,
        subtype,
        amount: item?.amount,
        units: 1,
        salesTax: 0,
        adjustment: item.adjustment || null,
        adjustmentId: item.adjustmentId || null,
      };
    }
    case TransactionItemSubtypeEnum.Adjustment: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Credit,
        subtype,
        amount: item?.amount,
        units: item?.units ?? 1,
        salesTax: 0,
        adjustment: item.adjustment ?? null,
        packageId: item?.packageId || null,
        parentPackageId: item?.parentPackageId || null,
        adjustmentId: item?.adjustmentId || null,
      };
    }
    case TransactionItemSubtypeEnum.NoShow: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.amount,
        units: 1,
        salesTax: 0,
      };
    }
    case TransactionItemSubtypeEnum.Canceled: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.amount,
        units: 1,
        salesTax: 0,
      };
    }
    case TransactionItemSubtypeEnum.PatientRefund: {
      return {
        id: item.id ?? -1,
        patientTransactionId: item.patientTransactionId ?? -1,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.amount,
        units: 1,
        salesTax: 0,
      };
    }
    case TransactionItemSubtypeEnum.PatientFeeRefund: {
      return {
        id: item.id ?? -1,
        patientTransactionId: item.patientTransactionId ?? -1,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.amount,
        units: 1,
        salesTax: 0,
      };
    }
    case TransactionItemSubtypeEnum.RefundUnappliedPayment: {
      return {
        id: item.id ?? -1,
        patientTransactionId: item.patientTransactionId ?? -1,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Credit,
        subtype,
        amount: item.amount,
        units: 1,
        salesTax: 0,
      };
    }
    case TransactionItemSubtypeEnum.AppliedPayment: {
      return {
        id: item.id ?? -1,
        patientTransactionId: item.patientTransactionId ?? -1,
        description: `Payment${item.paymentAmount < 0 ? ' (rollback)' : ''}`,
        type: TransactionItemTypeEnum.Debit,
        subtype: TransactionItemSubtypeEnum.AppliedPayment,
        amount: item.paymentAmount,
        taxAmount: item.taxAmount,
        units: 1,
        salesTax: false,
        paymentToward: item.paymentToward,
        paymentSource: item.paymentSource,
        createdAt: item.createdAt,
      };
    }
    case TransactionItemSubtypeEnum.PayorWriteOff: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: 'Claims Write-off',
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Credit,
        subtype,
        amount: item.amount,
        units: 1,
      };
    }
    case TransactionItemSubtypeEnum.PayorWriteOffReversal: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: 'Claims Write-off Reversal',
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.amount,
        units: 1,
      };
    }
    case TransactionItemSubtypeEnum.BalanceAllocatedToPatient: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: 'Balance Allocated to Patient',
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.amount,
        units: 1,
      };
    }
    case TransactionItemSubtypeEnum.Package: {
      return {
        id: item?.id ?? -1,
        patientTransactionId: -1,
        description: item.name,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.price,
        units: 1,
        salesTax: item.collectTax,
        packageId: item.packageId,
        packageDate: item.packageDate,
        membershipSetup: item.membershipSetup ?? null,
      };
    }
  }

  return item;
};

class TransactionActionClass {
  id: string;
  ts?: number | null;
  displayName?: string;

  constructor(action: TransactionActionType) {
    this.id = action.id || '- unknown -';
    this.ts = action.ts;
    this.displayName = action.displayName || '- unknown -';
  }
}
